import { DefaultButton, FocusTrapZone, IRefObject, ITextField, Layer, Overlay, Popup, PrimaryButton, Stack, TextField, Text, MessageBarType } from "@fluentui/react";
import { errorStyles, lgErrorStyles, lgPopupContentStyles, lgResendButtonStyles, lgSubTitleStyles, lgTextFieldStyles, lgTitleStyles, lgValidateButtonStyles, mdErrorStyles, mdPopupContentStyles, mdResendButtonStyles, mdSubTitleStyles, mdTextFieldStyles, mdTitleStyles, mdValidateButtonStyles, popupContainerStyles, popupContentStyles, resendButtonStyles, smErrorStyles, smPopupContentStyles, smResendButtonStyles, smSubTitleStyles, smTextFieldStyles, smTitleStyles, smValidateButtonStyles, subTitleStyles, textFieldStyles, titleStyles, validateButtonStyles, xlErrorStyles, xlPopupContentStyles, xlResendButtonStyles, xlSubTitleStyles, xlTextFieldStyles, xlTitleStyles, xlValidateButtonStyles, xxlErrorStyles, xxlPopupContentStyles, xxlResendButtonStyles, xxlSubTitleStyles, xxlTextFieldStyles, xxlTitleStyles, xxlValidateButtonStyles } from "./Styles/CommonOTPDialogStyles";
import { useBreakpoint } from "../Container/ContainerStyles";
import { BreakpointType } from "../Container/ContainerFluid";
import { useContext, useRef, useState } from "react";
import { t } from "i18next";
import { RegisterMessageBar } from "../Register/Components/Common/RegisterErrorBar";
import { ISessionStateContext, SessionStateContext } from "../../../Core/State/SessionStateContext";

export interface ICommonOTPDialogProps {
    hidden: boolean;
    validationInProgress: boolean;
    status: OTPStatus;
    onValidateCode: (code: string) => void;
    onResendCode: () => void;
}

export enum OTPStatus {
    Empty,
    Valid,
    NotValid,
    ResendCode
};

export const CommonOTPDialog: React.FC<ICommonOTPDialogProps> = (props: ICommonOTPDialogProps) => {
    const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
    const textFieldRefs = useRef<ITextField[] | null>([]);
    const sessionInfoContext: ISessionStateContext = useContext(SessionStateContext);

    const isSM = useBreakpoint(BreakpointType.sm);
    const isMD = useBreakpoint(BreakpointType.md);
    const isLG = useBreakpoint(BreakpointType.lg);
    const isXL = useBreakpoint(BreakpointType.xl);
    const isXXL = useBreakpoint(BreakpointType.xxl);

    const handleCodeChange = (index: number, value: string) => {
        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);

        if (value) {
            // Move focus to the next TextField if a character is entered
            if (index < textFieldRefs.current!.length - 1) {
                textFieldRefs.current![index + 1].focus();
            }
        } else {
            // Move focus to the previous TextField if the current one is cleared
            if (index > 0) {
                textFieldRefs.current![index - 1].focus();
            }
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        const pasteData = e.clipboardData.getData('text');
        if (pasteData.length === code.length) {
            const newCode = pasteData.split('');
            setCode(newCode);
            newCode.forEach((char, index) => {
                if (textFieldRefs.current![index]) {
                    textFieldRefs.current![index].focus();
                }
            });
        }
    };

    const handleValidateCode = () => {
        const codeToValidate: string = code.join('');
        props.onValidateCode(codeToValidate);
    };

    return (
        <>
            {
                props.hidden ?
                    <></>
                :
                    <Layer>
                        <Popup
                            className={popupContainerStyles}
                            role="dialog"
                            aria-modal="true"
                        >
                            <FocusTrapZone>
                                <Stack
                                    className={
                                        isSM ? 
                                            smPopupContentStyles :
                                        isMD ?
                                            mdPopupContentStyles :
                                        isLG ?
                                            lgPopupContentStyles :
                                        isXL ?
                                            xlPopupContentStyles :
                                        isXXL ?
                                            xxlPopupContentStyles 
                                        :
                                            popupContentStyles
                                    }
                                >
                                    <Stack>
                                        <Text
                                            className={
                                                isSM ? 
                                                    smTitleStyles :
                                                isMD ? 
                                                    mdTitleStyles :
                                                isLG ? 
                                                    lgTitleStyles :
                                                isXL ?
                                                    xlTitleStyles :
                                                isXXL ?
                                                    xxlTitleStyles
                                                :
                                                    titleStyles
                                            }
                                        >
                                            {t("common:otp:title")}
                                        </Text>
                                        <Text
                                            className={
                                                isSM ? 
                                                    smSubTitleStyles :
                                                isMD ? 
                                                    mdSubTitleStyles :
                                                isLG ?
                                                    lgSubTitleStyles :
                                                isXL ?
                                                    xlSubTitleStyles :
                                                isXXL ?
                                                    xxlSubTitleStyles
                                                :
                                                    subTitleStyles
                                            }
                                        >
                                            {t("common:otp:description", {email: sessionInfoContext.currentUser.email})}
                                        </Text>
                                    </Stack>
                                    <Stack
                                        verticalFill
                                        verticalAlign="space-between"
                                    >
                                        <Stack
                                            horizontal
                                            horizontalAlign="space-evenly"
                                        >
                                            {code.map((digit, index) => (
                                                <TextField
                                                    key={index}
                                                    maxLength={1}
                                                    value={digit}
                                                    componentRef={(ref) => textFieldRefs.current![index] = ref!}
                                                    onChange={(e, value) => handleCodeChange(index, value!)}
                                                    onPaste={handlePaste}
                                                    disabled={props.validationInProgress}
                                                    styles={
                                                        isSM ? 
                                                            smTextFieldStyles :
                                                        isMD ?
                                                            mdTextFieldStyles :  
                                                        isLG ?
                                                            lgTextFieldStyles :
                                                        isXL ?
                                                            xlTextFieldStyles : 
                                                        isXXL ?
                                                            xxlTextFieldStyles
                                                        :
                                                            textFieldStyles
                                                    }
                                                />
                                            ))}
                                        </Stack>
                                        <RegisterMessageBar 
                                            type={props.status == OTPStatus.NotValid ? MessageBarType.error : MessageBarType.info}
                                            message={props.status == OTPStatus.NotValid  ? t("common:otp:error") : t("common:otp:info")}
                                            visible={props.status == OTPStatus.NotValid || props.status == OTPStatus.ResendCode}
                                        />
                                        <Stack
                                            horizontal
                                            horizontalAlign="space-between"
                                        >
                                            <DefaultButton
                                                onClick={() => props.onResendCode()}
                                                disabled={props.validationInProgress}
                                                styles={
                                                    isSM ?
                                                        smResendButtonStyles :
                                                    isMD ?
                                                        mdResendButtonStyles :
                                                    isLG ?
                                                        lgResendButtonStyles :
                                                    isXL ?
                                                        xlResendButtonStyles :
                                                    isXXL ?
                                                        xxlResendButtonStyles
                                                    :
                                                        resendButtonStyles
                                                }
                                            >
                                                {t("common:otp:resendCode")}
                                            </DefaultButton>
                                            <PrimaryButton
                                                onClick={() => handleValidateCode()}
                                                disabled={props.validationInProgress}
                                                styles={
                                                    isSM ?
                                                        smValidateButtonStyles :
                                                    isMD ?
                                                        mdValidateButtonStyles :
                                                    isLG ?
                                                        lgValidateButtonStyles :
                                                    isXL ?
                                                        xlValidateButtonStyles :
                                                    isXXL ?
                                                        xxlValidateButtonStyles
                                                    :
                                                        validateButtonStyles
                                                }
                                            >
                                                {t("common:otp:validateCode")}
                                            </PrimaryButton>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </FocusTrapZone>
                        </Popup>
                    </Layer>
            }
        </>
    );
};